import { useState } from "preact/hooks";
import { addDays, fmtDateTime, get, html, localDate, patch, post, taskPresets } from "../lib.js";
import { Empty, Field, Loading, PageHeader, useAction, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";
import { tr } from "../i18n.js";

/** Groups open tasks the way people plan their day: overdue, today, tomorrow, this week, later. */
function bucketOf(task, tz) {
  const now = Date.now();
  const due = Date.parse(task.due_at);
  if (due < now) return "Overdue";
  const today = localDate(new Date(), tz);
  const day = localDate(new Date(task.due_at), tz);
  if (day === today) return "Today";
  if (day === addDays(today, 1)) return "Tomorrow";
  if (day <= addDays(today, 7)) return "This week";
  return "Later";
}
const BUCKETS = ["Overdue", "Today", "Tomorrow", "This week", "Later"];

export function TasksPage({ t, onChange }) {
  const [scope, setScope] = useState("me");
  const [data, reload] = useLoad(() => get(`${t.base}/tasks?status=open${scope === "me" ? "&assignee=me" : ""}`), [t.id, scope]);
  const [busy, run] = useAction();
  const done = async (task) => {
    await run(() => patch(`${t.base}/tasks/${task.id}`, { status: "done" }), tr("Task done"));
    reload();
    onChange?.();
  };
  const groups = {};
  for (const task of data?.tasks ?? []) (groups[bucketOf(task, t.timezone)] ??= []).push(task);

  return html`<div>
    <${PageHeader} title=${tr("Tasks")} subtitle=${tr("Follow-ups your team promised customers")} />
    <${PageGuide} id="tasks" />
    <div class="chips">
      <button class=${scope === "me" ? "chip active" : "chip"} onClick=${() => setScope("me")}>${tr("Mine")}</button>
      <button class=${scope === "all" ? "chip active" : "chip"} onClick=${() => setScope("all")}>${tr("Everyone")}</button>
    </div>
    ${!data && html`<${Loading} />`}
    ${data && !data.tasks.length && html`<${Empty} title=${tr("Nothing to follow up")}>${tr("Add a task from a conversation's customer panel (\"Call back tomorrow\", \"Send the price list\").")}</${Empty}>`}
    ${BUCKETS.filter((b) => groups[b]).map((b) => html`<section class="card">
      <h2>${tr(b)} <span class="muted small">${groups[b].length}</span></h2>
      <table class="table"><tbody>${groups[b].map((task) => html`<tr>
        <td><strong dir="auto">${task.title}</strong>
          <div class="muted small">${task.conversation_id ? html`<a href=${`/inbox/${task.conversation_id}`}>${task.contact_name || task.contact_phone || tr("Customer")}</a>` : task.contact_name || tr("Customer")} · ${task.contact_stage}</div></td>
        <td class="nowrap small ${b === "Overdue" ? "error-text" : ""}">${fmtDateTime(task.due_at, t.timezone)}</td>
        <td class="small">${task.assignee_name ?? html`<span class="muted">${tr("Unassigned")}</span>`}</td>
        <td class="row-actions"><button class="btn small" disabled=${busy} onClick=${() => done(task)}>${tr("Done")}</button></td>
      </tr>`)}</tbody></table>
    </section>`)}
  </div>`;
}

/** Open tasks for one contact plus a quick-add form; used in the inbox's customer panel. */
export function ContactTasks({ t, contactId, onChange }) {
  const [data, reload] = useLoad(() => get(`${t.base}/tasks?status=open&contact_id=${contactId}`), [contactId]);
  const presets = taskPresets(t.timezone);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState(presets[1].due);
  const [busy, run] = useAction();
  const add = async (e) => {
    e.preventDefault();
    await run(() => post(`${t.base}/tasks`, { contact_id: contactId, title, due }), tr("Task added"));
    setTitle("");
    reload();
    onChange?.();
  };
  const done = async (id) => {
    await run(() => patch(`${t.base}/tasks/${id}`, { status: "done" }));
    reload();
    onChange?.();
  };
  return html`<div>
    ${data?.tasks.length
      ? html`<ul class="plain">${data.tasks.map((task) => html`<li class="small task-line">
          <label><input type="checkbox" disabled=${busy} onChange=${() => done(task.id)} /> <span dir="auto">${task.title}</span></label>
          <span class=${Date.parse(task.due_at) < Date.now() ? "error-text" : "muted"}>${fmtDateTime(task.due_at, t.timezone)}${task.assignee_name ? ` · ${task.assignee_name}` : ""}</span>
        </li>`)}</ul>`
      : html`<p class="muted small">${tr("No open tasks.")}</p>`}
    <form onSubmit=${add} class="task-add">
      <${Field} label=${tr("New task")}><input dir="auto" required maxlength="200" placeholder=${tr("Call back about the price")} value=${title} onInput=${(e) => setTitle(e.target.value)} /></${Field}>
      <div class="chips">${presets.map((p) => html`<button type="button" class=${due === p.due ? "chip active" : "chip"} onClick=${() => setDue(p.due)}>${tr(p.label)}</button>`)}</div>
      <button class="btn small" disabled=${busy || !title.trim()}>${tr("Add task")}</button>
    </form>
  </div>`;
}
